import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Phone, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { chatsPollInterval, shownPhone } from '@/entities/chats';
import { listCrmContacts, type CrmContactListItem } from '@/entities/crm-contacts';
import { ApiError } from '@/shared/api';
import { formatBoardDate, labelOf, useI18n } from '@/shared/i18n';
import { Input } from '@/shared/ui/shadcn/input';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import { PageHeader } from '@/widgets/page-header';

function MetaRow({ icon: Icon, children }: { icon: typeof Phone; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <Icon className="size-3.5 shrink-0 opacity-70" aria-hidden />
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
}

function ContactCard({
  contact,
  onOpen,
}: {
  contact: CrmContactListItem;
  onOpen: (contact: CrmContactListItem) => void;
}) {
  const { t, locale } = useI18n();
  const phone = shownPhone(contact.phone) || contact.phone?.trim() || '';
  const channel = labelOf(t.channels, contact.channel);
  const contactLine = [phone, channel].filter(Boolean).join(' · ');
  const date = formatBoardDate(contact.updated_at, locale);
  return (
    <button
      type="button"
      onClick={() => onOpen(contact)}
      className="w-full rounded-xl border border-border/80 bg-background p-3 text-left shadow-none transition-colors hover:bg-muted/40"
    >
      <p className="truncate text-sm font-medium tracking-tight">
        {contact.name || t.crm.contactTitle}
      </p>
      <div className="mt-2 flex flex-col gap-1.5">
        {date ? <MetaRow icon={Calendar}>{date}</MetaRow> : null}
        {contactLine ? <MetaRow icon={Phone}>{contactLine}</MetaRow> : null}
        <MetaRow icon={Users}>{t.crm.contactCards(contact.card_count)}</MetaRow>
      </div>
    </button>
  );
}

export function ContactsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const deferredQuery = useMemo(() => query.trim(), [query]);

  const contactsQuery = useQuery({
    queryKey: ['crm-contacts', deferredQuery],
    queryFn: () => listCrmContacts(deferredQuery),
    refetchInterval: chatsPollInterval,
  });

  const contacts = contactsQuery.data ?? [];
  const missing = contactsQuery.error instanceof ApiError;

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
      <PageHeader
        title={t.nav.contacts}
        description={t.crm.contactDescription}
        actions={
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.crm.contactsSearch}
            className="h-8 w-44 desktop:w-56"
            aria-label={t.crm.contactsSearch}
          />
        }
      />
      <ScrollArea className="min-h-0 flex-1">
        {contactsQuery.isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">{t.common.loading}</p>
        ) : missing ? (
          <p className="p-4 text-sm text-destructive">{t.crm.contactsLoadFailed}</p>
        ) : contacts.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">{t.crm.contactsGalleryEmpty}</p>
        ) : (
          <div className="grid gap-3 p-4 sm:grid-cols-2 desktop:grid-cols-3 xl:grid-cols-4">
            {contacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onOpen={(item) => navigate(`/contacts/${encodeURIComponent(item.id)}`)}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
